// utils
import { utilHelper } from "../../../jwt/_helpers";
import {
    addAllItems,
    addItem, changeAllItems,
    changeListItem,
    removeItem,
    setForm, setItem,
    setItems,
    setLoading,
    setTableParams
} from "./IndexedActions";

// components
import {showToastCustom} from "../../../components/toast/customToast";

// store
import { store } from "../../store";

// labels
import { deleteErrorMsg, error } from "../../../components/i18n/i18n";


/**
 * `genericActions` es un objeto que contiene métodos para manejar operaciones CRUD comunes.
 * Cada método despacha acciones al almacen Redux para manejar el estado de la aplicación.
 * @namespace genericActions
 */
export const genericIndexedActions = {
    /**
     * Busca elementos basado en el filtro proporcionado.
     * Despacha acciones para establecer el estado de carga, actualizar elementos y parámetros de la tabla en el almacen.
     * @function search
     * @memberof genericActions
     * @param {string} path - La ruta de almacenamiento de los elementos.
     * @param {Object} provider - El proveedor a usar para la operación de búsqueda.
     * @param {number} pageIndex - El índice de la página a recuperar.
     * @param {number} pageSize - El número de elementos por página.
     * @param {Object} filter - Los criterios de filtro.
     * @param {function} callBack - Función a ejecutar al finalizar el proceso de búsqueda.
     */
    search : (path, provider, pageIndex, pageSize, filter, callBack = ()=>{}) => {
        store.dispatch(setLoading(path, true))
        provider.search(pageIndex, pageSize, filter)
            .then(result => {
                if (!result)
                    return

                store.dispatch(setItems(path, result.data || result))
                store.dispatch(setTableParams(path, pageSize, pageIndex, (result?.filtered !== pageSize ? result?.filtered : result?.total) ))
            })
            .catch(errorMessage => {
                console.error("Error",error, errorMessage)
                showToastCustom(error, errorMessage);
            })
            .finally(() => {
                store.dispatch(setLoading(path, false))
                callBack()
            })
    },


    /**
     * Obtiene un elemento basado en su ID.
     * Despacha acciones para establecer el estado de carga y actualizar el elemento en el almacen.
     * @function get
     * @memberof genericActions
     * @param {string} path - La ruta de almacenamiento de los elementos.
     * @param {Object} provider - El proveedor a usar para la operación de obtención.
     * @param {string|number} id - El id del elemento a obtener.
     * @param {Object} fbDatabase - La base de datos para obtener el elemento.
     * @param callBack
     */
    get : (path, provider, id, fbDatabase = null, callBack = ()=>{}) => {
        store.dispatch(setLoading(path, true))
        provider.getItem(id, fbDatabase)
            .then(result => {
                if (!result)
                    return

                if (result)
                    store.dispatch( changeListItem(path, result) )

                store.dispatch(setItem(path,  result))
            })
            .catch(errorMessage => {
                console.error("Error",error)
                showToastCustom(error, errorMessage);
            })
            .finally(() => {
                store.dispatch(setLoading(path, false))
                callBack()
            })
    },

    /**
     * Guarda un nuevo elemento.
     * Despacha acciones para establecer el estado de carga, actualizar el estado del formulario y agregar el nuevo elemento al almacen.
     * @function save
     * @memberof genericActions
     * @param {string} path - La ruta de almacenamiento de los elementos.
     * @param {Object} provider - El proveedor a usar para la operación de guardar.
     * @param {Object} data - Los datos del elemento a guardar.
     * @param {function} callBack - Función a ejecutar al finalizar el proceso de guardado.
     */
    save: (path, provider, data, callBack = ()=>{}) => {
        store.dispatch(setLoading(path, true))

        provider.saveItem(data)
            .then(result => {
                store.dispatch( setForm(path, false, true, true) )
                if(result)
                    store.dispatch( addItem(path, result) )
            })
            .catch(errorMessage => {
                console.error("Error",error)
                showToastCustom(error, errorMessage);
            })
            .finally(() => {
                store.dispatch(setLoading(path, false))
                callBack()
            })

    },

    saveAll: (path, provider, data, callBack = ()=>{}) => {
        store.dispatch(setLoading(path, true))

        provider.saveAllItems(data)
            .then(result => {
                store.dispatch( setForm(path, false, true, true) )

                if(result)
                    store.dispatch( addAllItems(path, result) )
            })
            .catch(errorMessage => {
                console.error("Error",error)
                showToastCustom(error, errorMessage);
            })
            .finally(() => {
                store.dispatch(setLoading(path, false))
                callBack()
            })
    },

    /**
     * Actualiza un elemento existente.
     * Despacha acciones para establecer el estado de carga, actualizar el estado del formulario y cambiar el elemento en el almacen.
     * @function update
     * @memberof genericActions
     * @param {string} path - La ruta de almacenamiento de los elementos.
     * @param {Object} provider - El proveedor a usar para la operación de actualización.
     * @param {string|number} id - El id del elemento a actualizar.
     * @param {Object} data - Los nuevos datos para el elemento.
     * @param {function} callBack - Función a ejecutar al finalizar el proceso de actualización.
     */
    update : (path, provider, id, data,callBack = ()=>{}) => {
        store.dispatch(setLoading(path, true))

        provider.updateItem(id, data)
            .then(result => {
                store.dispatch( setForm(path, false, true, true) )
                if (result)
                    store.dispatch( changeListItem(path, result) )
            })
            .catch(errorMessage => {
                console.error("Error",errorMessage)
                showToastCustom(error, errorMessage);
            })
            .finally(() => {
                store.dispatch(setLoading(path, false))
                callBack()
            })
    },

    updateAll : (path, provider, data, callBack = ()=>{}) => {
        store.dispatch(setLoading(path, true))

        provider.updateAllItems(data)
            .then(result => {
                store.dispatch( setForm(path, false, true, true) )
                if (result)
                    store.dispatch( changeAllItems(path, result) )
            })
            .catch(errorMessage => {
                console.error("Error",error)
                showToastCustom(error, errorMessage);
            })
            .finally(() => {
                store.dispatch(setLoading(path, false))
                callBack()
            })
    },

    /**
     * Elimina un elemento existente.
     * Despacha acciones para establecer el estado de carga y eliminar el elemento del almacen.
     * @function remove
     * @memberof genericActions
     * @param {string} path - La ruta de almacenamiento de los elementos.
     * @param {Object} provider - El proveedor a usar para la operación de eliminación.
     * @param {string|number} id - El id del elemento a eliminar.
     * @param {Object} db - La base de datos para eliminar el elemento.
     * @param {function} callBack - Función a ejecutar al finalizar el proceso de eliminación.
     */
    delete: (path, provider, id, db, callBack = ()=>{}) => {
        store.dispatch(setLoading(path, true))

        provider.deleteItem(id, db)
            .then(result => {
                if(result?.status)
                    store.dispatch( removeItem(path, id) )

                if (utilHelper.hasAssociateRecords()) {
                    showToastCustom(error, deleteErrorMsg, true);
                    utilHelper.restartAssociateRecords();
                }
            })
            .catch(errorMessage => {
                console.error("Error",error)
                showToastCustom(error, errorMessage);
            })
            .finally(() => {
                store.dispatch(setLoading(path, false))
                callBack()
            })
    },

    hardDelete: (path, provider, data ) => {
        store.dispatch(setLoading(path, true))

        provider.hardDelete(data)
            .then(result => {
                if(result?.status) {
                    if (data?.id !== null)
                        store.dispatch(removeItem(path, data.id))
                    else
                        store.dispatch(removeItem(path, data))
                }

                if (utilHelper.hasAssociateRecords()) {
                    showToastCustom(error, deleteErrorMsg, true);
                    utilHelper.restartAssociateRecords();
                }
            })
            .catch(errorMessage => {
                console.error("Error", error)
                showToastCustom(error, errorMessage);
            })
            .finally(() => {
                store.dispatch(setLoading(path, false))
                //callBack()
            })
    },

    /**
     * Reordena los elementos.
     * Despacha acciones para establecer el estado de carga, actualizar el estado del formulario y cambiar el orden de los elementos en el almacen.
     * @function reorder
     * @memberof genericActions
     * @param {string} path - La ruta de almacenamiento de los elementos.
     * @param {Object} provider - El proveedor a usar para la operación de reordenamiento.
     * @param {Array} data - Los datos de los elementos a reordenar.
     * @param {function} callBack - Función a ejecutar al finalizar el proceso de actualización.
     */
    reorder : (path, provider, data, callBack = ()=>{}) => {
        store.dispatch(setLoading(path, true))

        provider.reorderItems(data)
            .then(result => {
                store.dispatch( setForm(path, false, true, true) )

                if (result && result?.length > 0) {
                    result.map(_result => store.dispatch(changeListItem(path, _result)))
                }
            })
            .catch(errorMessage => {
                showToastCustom(error, errorMessage);
                console.error("Error",error)
            })
            .finally(() => {
                store.dispatch(setLoading(path, false))
                callBack()
            })
    },

    /**
     * Agrega un nuevo elemento.
     * Despacha acciones para establecer el estado de carga, actualizar el estado del formulario y agregar el nuevo elemento al almacen.
     * @function add
     * @memberof genericActions
     * @param {string} path - La ruta de almacenamiento de los elementos.
     * @param {Object} provider - El proveedor a usar para la operación de agregar.
     * @param {Object} data - Los datos del elemento a agregar.
     */
    add: (path, provider, data) => {
        store.dispatch(setLoading(path, true))

        provider.add(data)
            .then(result => {
                store.dispatch( setForm(path, false, true, true) )
                if(result)
                    store.dispatch( addItem(path, result) )
            })
            .catch(errorMessage => {
                showToastCustom(error, errorMessage);
                console.log("Error",error)
            })
            .finally(() => store.dispatch(setLoading(path, false)))

    },

    /**
     * Elimina un elemento existente.
     * Despacha acciones para establecer el estado de carga y eliminar el elemento del almacen.
     * @function remove
     * @memberof genericActions
     * @param {string} path - La ruta de almacenamiento de los elementos.
     * @param {Object} provider - El proveedor a usar para la operación de eliminación.
     * @param {Object} data - Los datos del elemento a eliminar.
     */
    remove: (path, provider, data) => {
        store.dispatch(setLoading(path, true))

        provider.remove(data)
            .then(result => {
                if(result?.status)
                    store.dispatch( removeItem( path, data.id ) )
            })
            .catch(errorMessage => {
                showToastCustom(error, errorMessage);
                console.log("Error",error)
            })
            .finally(() => store.dispatch(setLoading(path, false)))
    },

    removeFromFirebase: (path, provider, data) => {
        store.dispatch(setLoading(path, true))

        provider.removeFromFirebase(data)
            .then(result => {
                console.log("con path",{result})
                if(result)
                    store.dispatch( removeItem( path, result.id ) )
            })
            .catch(errorMessage => {
                showToastCustom(error, errorMessage);
                console.log("Error",error)
            })
            .finally(() => store.dispatch(setLoading(path, false)))
    },

    /**
     * Traduce un elemento existente.
     * Despacha acciones para establecer el estado de carga, actualizar el estado del formulario
     * y cambiar el elemento traducido en el almacen.
     *
     * @function translate
     * @memberof genericActions
     * @param {string} path - La ruta de almacenamiento de los elementos.
     * @param {Object} provider - El proveedor a usar para la operación de traducción.
     * @param {string|number} id - El id del elemento a traducir.
     * @param {Object} data - Los datos necesarios para la traducción.
     * @param {function} [callBack] - Función a ejecutar al finalizar el proceso de traducción.
     */
    translate: (path, provider, id, data, callBack = ()=>{}) =>{
        store.dispatch(setLoading(path, true))

        provider.translate(id, data)
            .then(result => {
                store.dispatch( setForm(path, false, true, true) )
                if (result?.status) {
                    // El modelo place no devuelve result.data, solo result
                    console.log("RESULT: ", result)
                    store.dispatch(changeListItem(path, ( result?.data || result )))
                }
            })
            .catch(errorMessage => {
                showToastCustom(error, errorMessage);
                console.log("Error",error)
            })
            .finally(() => {
                store.dispatch(setLoading(path, false))
                callBack()
            })
    },



    translateAllDishDrinkInMenu: (path, provider, id, data, callBack = ()=>{}) =>{
        store.dispatch(setLoading(path, true))

        provider.translateAllDishDrinkInMenu(id, data)
            .then(result => {
                store.dispatch( setForm(path, false, true, true) )
                if (result?.status) {
                    console.log("RESULT: ", result?.data)
                    store.dispatch(changeListItem(path, result?.data))
                }
            })
            .catch(errorMessage => {
                showToastCustom(error, errorMessage);
                console.log("Error",error)
            })
            .finally(() => {
                store.dispatch(setLoading(path, false))
                callBack()
            })
    },


    sync: (path, provider, data, callBack = ()=>{}) =>{
        store.dispatch(setLoading(path, true))

        provider.sync(data)
            .then(result => {
                store.dispatch( setForm(path, false, true, true) )
                if (result?.status) {
                    console.log("RESULT: ", result?.data)
                    store.dispatch(changeListItem(path, result?.data))
                }
            })
            .catch(errorMessage => {
                showToastCustom(error, errorMessage);
                console.log("Error",error)
            })
            .finally(() => {
                store.dispatch(setLoading(path, false))
                callBack()
            })
    }
}